import { ArrowUpRight, MessageCircle, Sparkles } from "lucide-react";
import Globe from "./Globe";
import { useI18n } from "@/i18n";

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Hero() {
  const { t } = useI18n();
  const badges = t("hero.badges") || [];
  const origin = t("hero.stat_origin");
  const reach = t("hero.stat_reach");
  const routes = t("hero.stat_routes");

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100vh] pt-32 pb-24 md:pt-36 md:pb-32 overflow-hidden noise"
    >
      <div className="glow-blue animate-pulseGlow" style={{ width: 620, height: 620, top: -160, left: -120, background: "radial-gradient(circle at center, rgba(22,75,255,0.55), transparent 60%)" }} />
      <div className="glow-blue animate-pulseGlow" style={{ width: 560, height: 560, top: 80, right: -160, background: "radial-gradient(circle at center, rgba(122,92,255,0.45), transparent 60%)" }} />
      <div className="glow-blue" style={{ width: 800, height: 240, left: "50%", bottom: -120, transform: "translateX(-50%)", background: "radial-gradient(ellipse at center, rgba(22,75,255,0.35), transparent 60%)" }} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="pill mb-6" data-testid="hero-eyebrow">
              <Sparkles className="w-3.5 h-3.5 text-[#8B6DFF]" />
              <span className="tracking-[0.18em] uppercase text-[11px] text-[#C9CAD1]">
                {t("hero.eyebrow")}
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display text-[40px] sm:text-[54px] lg:text-[62px] leading-[1.07] font-extrabold tracking-tight"
            >
              <span className="text-white">{t("hero.headline_a")}</span>{" "}
              <span className="text-gradient-brand">{t("hero.headline_b")}</span>
              <br />
              <span className="text-white">{t("hero.headline_c")}</span>
              <br />
              <span className="text-[#A8A8B3]">{t("hero.headline_d")}</span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-6 max-w-xl text-[16px] md:text-[17px] leading-relaxed text-[#A8A8B3]"
            >
              {t("hero.subheadline")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button data-testid="hero-cta-quote" onClick={() => scrollTo("contact")} className="btn-primary">
                {t("common.request_quote")} <ArrowUpRight className="w-4 h-4" />
              </button>
              <button data-testid="hero-cta-contact" onClick={() => scrollTo("contact")} className="btn-ghost">
                {t("common.contact_us")} <MessageCircle className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-9 flex flex-wrap gap-2.5" data-testid="hero-badges">
              {badges.map((b, i) => (
                <span key={i} className="pill" data-testid={`hero-badge-${i}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7A5CFF] shadow-[0_0_10px_rgba(122,92,255,0.9)]" />
                  {b}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 reveal">
            <div className="relative w-full max-w-[600px] mx-auto">
              <Globe size={600} />
              <FloatingStat className="absolute -left-2 sm:-left-4 top-[18%] animate-floatY" label={origin.label} value={origin.value} testId="hero-stat-origin" />
              <FloatingStat className="absolute right-0 sm:-right-2 top-[44%] animate-floatY2" label={reach.label} value={reach.value} testId="hero-stat-reach" />
              <FloatingStat className="absolute left-[8%] bottom-[8%] animate-floatY" label={routes.label} value={routes.value} testId="hero-stat-routes" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FloatingStat({ className = "", label, value, testId }) {
  return (
    <div data-testid={testId} className={`glass-card rounded-2xl px-3.5 py-2.5 min-w-[140px] ${className}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B6DFF]">{label}</div>
      <div className="text-[13.5px] mt-0.5 text-white font-medium">{value}</div>
    </div>
  );
}
